import React, { useEffect, useMemo, useRef, useState } from 'react';
import useTomTomAutocomplete from '@hooks/useTomTomAutocomplete';
import { tomtomPlaceById } from '@api/tomtom';

export default function AddressInput({
  value,
  onChange,
  onSelect,
  countries = ['US', 'GB'],
  center, // { lat, lon }
  limit = 6,
  placeholder = 'Start typing an address...',
  disabled = false,
  className = '',
  dropdownClassName = '',
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);
  const boxRef = useRef(null);

  const countrySet = useMemo(() => countries.join(','), [countries]);
  const { items, loading, error } = useTomTomAutocomplete({
    query: value,
    limit,
    countrySet,
    lat: center?.lat,
    lon: center?.lon,
  });

  // close on outside click
  useEffect(() => {
    function onDocClick(e) {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // open suggestion list whenever there are items
  useEffect(() => {
    setOpen(Boolean(items?.length));
    setHighlight(0);
  }, [items]);

  async function handlePick(idx) {
    const picked = items[idx];
    if (!picked) return;

    try {
      let full = picked;

      if (picked.entityId) {
        const detail = await tomtomPlaceById(picked.entityId);
        const richer = detail?.results?.[0];
        if (richer) {
          full = {
            ...picked,
            raw: richer,
            address: {
              ...picked.address,
              freeform:
                richer?.address?.freeformAddress ?? picked.address.freeform,
              postalCode:
                richer?.address?.postalCode ?? picked.address.postalCode,
              country: richer?.address?.country ?? picked.address.country,
              countryCode:
                richer?.address?.countryCode ?? picked.address.countryCode,
              municipality:
                richer?.address?.municipality ?? picked.address.municipality,
              streetName:
                richer?.address?.streetName ?? picked.address.streetName,
              streetNumber:
                richer?.address?.streetNumber ?? picked.address.streetNumber,
              countrySubdivision:
                richer?.address?.countrySubdivision ??
                picked.address.countrySubdivision,
            },
            position: richer?.position ?? picked.position,
            label:
              richer?.address?.freeformAddress ||
              richer?.poi?.name ||
              picked.label,
          };
        }
      }

      // 👇 only show "house number + street" in the input
      const streetLine =
        [full.address?.streetNumber, full.address?.streetName]
          .filter(Boolean)
          .join(' ') || full.label;

      onSelect?.(full); // pass full details to parent
      onChange?.(streetLine); // reflect only the street line in the input
      setOpen(false);
    } catch (e) {
      console.error('[TomTom] pick error:', e);

      const streetLine =
        [picked.address?.streetNumber, picked.address?.streetName]
          .filter(Boolean)
          .join(' ') || picked.label;

      onSelect?.(picked);
      onChange?.(streetLine);
      setOpen(false);
    }
  }

  function onKeyDown(e) {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handlePick(highlight);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div ref={boxRef} className={`dropdown position-relative ${className}`}>
      {/* Input acts as the trigger (no dropdown button) */}
      <input
        ref={inputRef}
        type='text'
        className='form-control'
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onFocus={() => {
          // 👇 Clear existing value when focusing
          if (value) onChange?.('');
          // also open dropdown if suggestions are ready
          if (items?.length) setOpen(true);
        }}
        onKeyDown={onKeyDown}
        disabled={disabled}
        autoComplete='off'
      />

      {/* Loading indicator (optional) */}
      {loading && (
        <div className='position-absolute top-50 end-0 translate-middle-y pe-3 small text-muted'>
          <span className='spinner-border spinner-border-sm' />
        </div>
      )}

      {/* Bootstrap dropdown menu */}
      {open && (
        <div
          className={`dropdown-menu show ${dropdownClassName}`}
          style={{
            margin: 0,
            position: 'absolute',
            inset: '0px auto auto 0px',
            transform: 'translate(0px, 39px)', // ~ input height
            zIndex: 1055,
            maxHeight: 150,
            overflowY: 'auto',
            width: '100%',
          }}
          role='menu'
        >
          {items.map((it, idx) => (
            <button
              key={`${it.entityId || it.label}-${idx}`}
              type='button'
              className={`dropdown-item d-flex justify-content-between align-items-center ${idx === highlight ? 'active' : ''}`}
              onMouseEnter={() => setHighlight(idx)}
              onMouseDown={(e) => e.preventDefault()} // keep input focus
              onClick={() => handlePick(idx)}
            >
              <div className='text-truncate'>
                {it.label || '(Unnamed)'}
                <div className='small text-muted text-truncate'>
                  {it.address?.municipality || it.address?.country || ''}
                </div>
              </div>
              {it.position && (
                <span className='badge bg-light text-muted ms-2'>
                  {Number(it.position.lat).toFixed(3)},{' '}
                  {Number(it.position.lon).toFixed(3)}
                </span>
              )}
            </button>
          ))}

          {!items.length && !loading && !error && (
            <div className='dropdown-item text-muted small'>No matches</div>
          )}
          {error && (
            <div className='dropdown-item text-danger small'>
              {error.message || 'Autocomplete failed'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
